import { AppRegistrationSection } from '../../../AppRegistrationSection';
import { Base64 } from 'js-base64';
import { ButtonActor, Modal } from 'components';
import {
  CheckGreenRoundedIcon,
  ClockBoldIcon,
  InformationIcon,
  LockIcon,
  TinkBankIcon,
  TinkIcon,
} from 'components/icons';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../../AppConfig';
import { getFetchHeaders } from '../../../utils/fetchHeaders';
import { getScreenTranslation } from '../../../utils/translations';
import { JUICY_PING_BTN, SESSION_TINK_REASON } from 'utils/constants';
import { redirection401ToLogin, useGetClientApplication } from '../../../api';
import { routes } from '../../../routes';
import { Tink } from 'components/Tink/Tink';
import { TinkMessageEvent } from 'components/Tink/parseTinkMessageEvent';
import { useErrorManager } from 'InternalLibrary';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Card from 'components/atoms/Card/Card';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import smartlookClient from 'smartlook-client';
import styles from './OnlineBankVerification.module.scss';
import Text from 'components/atoms/Typography/Text';
import TinkEvent from '../../../hooks/eventCollector/events/TinkEvent';
import Title from 'components/atoms/Title/Title';
import useClient from 'hooks/useClient';
import useEventCollector from '../../../hooks/eventCollector/useEventCollector';
import useTink from 'hooks/useTink';

export default function OnlineBankVerification() {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isModalOpened, setIsModalOpened] = useState(false);

  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const i18Next = useTranslation();
  const navigate = useNavigate();

  const apiClientApplication = useGetClientApplication();
  const { client, fetchClient } = useClient();
  const { getLink, sendReportIds } = useTink();

  const [applicationIdAndCompleted, setApplicationIdAndCompleted] = useState({
    id: apiClientApplication.data?.id,
    completed: apiClientApplication.data?.completed,
  });
  const [clientPersonalId, setClientPersonalId] = useState(client?.personalId);

  const { setError } = useErrorManager();
  const { track } = useEventCollector();

  const location = useLocation();

  const toggleVideoModal = useCallback(() => {
    setIsVideoOpen((prevState) => !prevState);
  }, [isVideoOpen]);

  useEffect(() => {
    const fetchMissing = async () => {
      if (!applicationIdAndCompleted.id || !clientPersonalId) {
        try {
          const fetchedApplication = await apiClientApplication.fetch({
            headers: getFetchHeaders({
              Accept: 'application/vnd.4finance.web.v1.hal+json',
              'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
            }),
          });
          if (fetchedApplication?.resolution) {
            navigate(routes.FirstLoan_Rejected);
          }

          const fetchedClient = await fetchClient();

          setApplicationIdAndCompleted({
            id: fetchedApplication?.id,
            completed: fetchedApplication?.completed,
          });
          setClientPersonalId((_) => fetchedClient?.personalId);
        } catch (error) {
          if (error.status === 401) {
            redirection401ToLogin();
          }
        }
      }
    };

    fetchMissing();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (searchParams.get('error_reason') === 'REQUEST_FAILED_CREATE_REPORT') {
      navigate(
        `${routes.FirstLoan_OnlineBank_DocumentsRequired}?${searchParams}`,
      );
      // eslint-disable-next-line no-console
      console.error(
        `Tink error. Search params: ${decodeURIComponent(location.search)}`,
      );
      return;
    }

    const errorParam = searchParams.get('error');

    if (errorParam && errorParam !== 'USER_CANCELLED') {
      navigate(`${routes.FirstLoan_OnlineBank_Error}?${searchParams}`);
      // eslint-disable-next-line no-console
      console.error(
        `Tink error. Search params: ${decodeURIComponent(location.search)}`,
      );
    }
  }, [location.search]);

  const handleTinkEvent = useCallback(
    async (payload: TinkMessageEvent) => {
      const { data, type, error } = payload;

      if (type !== 'status') {
        track(
          new TinkEvent({
            data,
            typeTink: type,
            error,
          }),
        );

        if (type === 'application-event' && Boolean(data.event))
          smartlookClient.track(`TINK_${data.event}`, {});
      }

      if (type === 'error') {
        // propagate tink event
        try {
          await sendReportIds({ type, ...error });
        } catch (error) {
          if (error.status === 401) {
            redirection401ToLogin();
          }
        }

        switch (error.status) {
          case 'USER_CANCELLED':
            setDialogOpen(false);
            return navigate(routes.FirstLoan_OnlineBank_Verify);
          case 'BAD_REQUEST': // Note: Seems Tink doesn't actually fires this error status.
          case 'INTERNAL_ERROR':
          case 'TEMPORARY_ERROR':
            setDialogOpen(false);
            setError(error);
            const queryParamsError = `?trackingId=${
              error.trackingId ?? ''
            }&reason=${error.reason ?? ''}&status=${
              error.status ?? ''
            }&tinkReportFailed=true`;

            sessionStorage.setItem(
              SESSION_TINK_REASON,
              Base64.encode(error.status),
            );

            if (
              error.reason === 'INACTIVITY_OPEN_APPLICATION_FRAUD_EVALUATED'
            ) {
              return navigate(`${routes.FirstLoan_Manual}${queryParamsError}`);
            }

            return navigate(
              `${routes.FirstLoan_OnlineBank_DocumentsRequired}${queryParamsError}`,
            );
          case 'AUTHENTICATION_ERROR':
            setDialogOpen(false);
            setError(error);
            const queryParamsAuthError = `?trackingId=${
              error.trackingId ?? ''
            }&reason=${error.reason ?? ''}&status=${error.status ?? ''}`;

            return navigate(
              `${routes.FirstLoan_OnlineBank_Verify}${queryParamsAuthError}`,
            );
        }
      } else {
        // Tink data to save on cola
        const tinkDataToPropagate = {
          transactionReportId: data?.transaction_report_id,
          riskInsightsId: data?.risk_insights_report_id,
        };

        if (type === 'view-event' && data?.screen === 'ERROR_SCREEN') {
          setDialogOpen(false);
          return navigate(routes.FirstLoan_OnlineBank_Verify);
        } else if (type === 'reports' && applicationIdAndCompleted.id) {
          // Send tink report
          await sendReportIds({
            loanApplicationId: applicationIdAndCompleted.id,
            ...tinkDataToPropagate,
          });

          return navigate(routes.FirstLoan_OnlineBank_Check);
        }
      }
    },
    [track, navigate, setError, clientPersonalId, applicationIdAndCompleted.id],
  );

  const onVerifyHandler = async () => {
    if (applicationIdAndCompleted?.completed !== true) {
      try {
        const fetchedApplication = await apiClientApplication.fetch({
          headers: getFetchHeaders({
            Accept: 'application/vnd.4finance.web.v1.hal+json',
            'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
          }),
        });
        if (fetchedApplication?.resolution === 'REJECTED') {
          navigate(routes.FirstLoan_Rejected);
        }
      } catch (error) {
        if (error.status === 401) {
          redirection401ToLogin();
        }
      }
    }

    setDialogOpen(true);
  };

  const onSubmit = useCallback(async () => {
    setLoading(true);
    await onVerifyHandler();
    setLoading(false);
  }, [setLoading]);

  const handleInfoIcon = () => {
    setIsModalOpened(true);
  };

  const content = getScreenTranslation('tink_verification', i18Next);

  const TinkCard = useMemo(
    () => (
      <Card className={styles['tink-card']}>
        <div className={styles['tink-card-info-section']}>
          <TinkIcon
            className={styles['tink-icon']}
            data-test="tinkVerification-tink-icon"
          />
          <div className={styles['tink-card-checkbox-section']}>
            <TinkBankIcon data-test="tinkVerification-tink-bank-icon" />
            <div className={styles['tink-card-checkbox-items']}>
              {Object.values(content.check_points).map((checkPoint, index) => (
                <div className={styles['tink-card-checkbox-item']} key={index}>
                  <CheckGreenRoundedIcon
                    data-test={`tinkVerification-check-greenRounded-icon-${index}`}
                  />
                  <Text
                    contentClickHandler={toggleVideoModal}
                    dataTest={`tinkVerification-check-greenRounded-text-${index}`}
                    dangerouslySetInnerHTML={{ __html: checkPoint as string }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles['tink-card-security-section']}>
          <Card className={styles['security-card']}>
            <Text
              className={styles['security-card-text']}
              dangerouslySetInnerHTML={{
                __html: content.info_box.security,
              }}
              dataTest="tinkVerification-security-text"
              kind="p"
              suppressDefaultStyles
            />
            <div className={styles['security-card-text-with-icon']}>
              <span
                className={styles['security-card-text']}
                data-test="tinkVerification-info-transfer-text"
              >
                {content.info_box.transfer}
                <div
                  className={styles['info-icon-modal-button']}
                  data-test="tinkVerification-info-transfer-text"
                  onClick={handleInfoIcon}
                >
                  <InformationIcon
                    className={styles['info-icon']}
                    data-test="tinkVerification-info-transfer-icon"
                  />
                </div>
              </span>
            </div>
          </Card>
          <ButtonActor
            buttonProps={{
              className: classes([styles['submit-button'], JUICY_PING_BTN]),
              dataTest: 'tinkVerification-info-transfer-btn',
              leftIcon: <LockIcon />,
              loading,
              onClick: onSubmit,
            }}
            className={styles['submit-button-wrapper']}
          >
            {content.submit_btn}
          </ButtonActor>
          <Text
            className={styles['submit-footer-text']}
            dangerouslySetInnerHTML={{
              __html: content.submit_footer,
            }}
            dataTest="tinkVerification-footer-text"
            level={3}
            kind="p"
          />
        </div>
      </Card>
    ),
    [content],
  );

  return (
    <>
      <Modal className={styles.modal} isOpen={isDialogOpen}>
        <Tink onTinkEvent={handleTinkEvent} link={getLink(true)} />
      </Modal>
      <Frame
        {...frameConfig}
        sectionID={AppRegistrationSection.Confirmation}
        bodyWrapperClassName={classes(
          layout['width-lg'],
          layout['frame-x-compact'],
          styles['frame-body-wrapper'],
        )}
        hideEditButton
      >
        {/* Modal Video Wrapper */}
        <Modal
          isOpen={isVideoOpen}
          className={styles['modal-video-wrapper']}
          onClose={toggleVideoModal}
          onCloseOutside={toggleVideoModal}
          onClickOutside={toggleVideoModal}
          isMobileFullWidth
        >
          <iframe
            src="https://www.youtube.com/embed/0mdcVWmr0HQ"
            className={styles['modal-video']}
            title={content.youtubeVideoPlayer}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;  web-share"
            allowFullScreen
          ></iframe>
        </Modal>
        {/* Modal */}
        <Modal
          isOpen={isModalOpened}
          onClose={() => setIsModalOpened(false)}
          className={classes(styles.modal, styles['modal-info'])}
        >
          <Text
            dangerouslySetInnerHTML={{
              __html: content.warning_footer_description,
            }}
          />
        </Modal>

        {/* Content */}
        <div
          className={classes(
            layout['frame-x-padding'],
            styles['first-section'],
          )}
        >
          <Title text={content.title} onTextClick={toggleVideoModal} />
          <Card intent="warning" className={styles['warning-card']}>
            <ClockBoldIcon />
            <Text
              weight="medium"
              level={1}
              kind="p"
              text={content.warning_card}
              className={styles['warning-card-text']}
            />
          </Card>
        </div>
        {TinkCard}
      </Frame>
    </>
  );
}
